import { DateBasis, ReportArea } from 'modules/arena/reports/model/types';

export interface StaticOption {
  id: string;
  label: string;
}

/** Filtro específico de uma área. */
export interface FilterSpec {
  key: string;
  label: string;
  /** Opções vindas do catálogo da arena (ex.: 'instructors') ou fixas. */
  catalogKey?: string;
  options?: StaticOption[];
  /** Sem opção "Todos" (ex.: data-base sempre tem um valor). */
  required?: boolean;
  defaultValue?: string;
  /** Depende de outro filtro específico (ex.: categoria depende do torneio). */
  parentKey?: string;
}

export const WEEKDAY_OPTIONS: StaticOption[] = [
  { id: '1', label: 'Segunda' },
  { id: '2', label: 'Terça' },
  { id: '3', label: 'Quarta' },
  { id: '4', label: 'Quinta' },
  { id: '5', label: 'Sexta' },
  { id: '6', label: 'Sábado' },
  { id: '0', label: 'Domingo' },
];

export const TIME_BAND_OPTIONS: StaticOption[] = [
  { id: 'manha', label: 'Manhã (06h–12h)' },
  { id: 'tarde', label: 'Tarde (12h–17h)' },
  { id: 'noite', label: 'Noite (17h–23h)' },
];

export const FINANCIAL_DATE_BASIS: StaticOption[] = [
  { id: 'venda', label: 'Data da venda' },
  { id: 'recebimento', label: 'Data do recebimento' },
  { id: 'vencimento', label: 'Data de vencimento' },
  { id: 'repasse', label: 'Data do repasse' },
];

export const DATE_BASIS_LABEL: Record<DateBasis | 'misto', string> = {
  venda: 'data da venda',
  recebimento: 'data do recebimento',
  vencimento: 'data de vencimento',
  repasse: 'data do repasse',
  aula: 'data da aula',
  avaliacao: 'data da avaliação (notas) e da aula (atividade)',
  reserva: 'data de uso da quadra',
  contrato: 'início do contrato',
  inscricao: 'data da inscrição',
  misto: 'definida em cada indicador',
};

export interface AreaDefinition {
  id: ReportArea;
  label: string;
  description: string;
  filters: FilterSpec[];
  /** Agrupamento por dia/semana/mês faz sentido (há série temporal). */
  supportsGrouping: boolean;
}

export const AREAS: AreaDefinition[] = [
  {
    id: 'visao-geral',
    label: 'Visão geral',
    description: 'Principais números do período. Clique em um indicador para abrir o detalhamento.',
    filters: [],
    supportsGrouping: true,
  },
  {
    id: 'financeiro',
    label: 'Financeiro e faturamento',
    description: 'Vendas, recebimentos, pendências, reembolsos e repasses — bruto e líquido da arena.',
    supportsGrouping: true,
    filters: [
      { key: 'dateBasis', label: 'Data de referência', options: FINANCIAL_DATE_BASIS, required: true, defaultValue: 'venda' },
      { key: 'product', label: 'Produto', catalogKey: 'products' },
      { key: 'paymentStatus', label: 'Situação do pagamento', catalogKey: 'paymentStatuses' },
      { key: 'paymentMethod', label: 'Meio de pagamento', catalogKey: 'paymentMethods' },
    ],
  },
  {
    id: 'aulas',
    label: 'Aulas e demanda',
    description: 'Aulas programadas e realizadas, presença, ocupação e espera por turma e horário.',
    supportsGrouping: true,
    filters: [
      { key: 'instructorId', label: 'Professor', catalogKey: 'instructors' },
      { key: 'classGroupId', label: 'Turma', catalogKey: 'classGroups', parentKey: 'instructorId' },
      { key: 'level', label: 'Nível', catalogKey: 'levels' },
      { key: 'weekday', label: 'Dia da semana', options: WEEKDAY_OPTIONS },
      { key: 'timeBand', label: 'Faixa de horário', options: TIME_BAND_OPTIONS },
    ],
  },
  {
    id: 'professores',
    label: 'Professores e avaliações',
    description: 'Nota média, quantidade de avaliações e atividade de cada professor no período.',
    supportsGrouping: false,
    filters: [],
  },
  {
    id: 'pacotes',
    label: 'Pacotes e alunos',
    description: 'Contratos, renovações, pagamentos pendentes e utilização das aulas contratadas.',
    supportsGrouping: false,
    filters: [
      { key: 'planId', label: 'Plano', catalogKey: 'plans' },
      { key: 'contractStatus', label: 'Situação do contrato', catalogKey: 'contractStatuses' },
    ],
  },
  {
    id: 'quadras',
    label: 'Quadras e reservas',
    description: 'Disponibilidade, tempo reservado, uso efetivo e cancelamentos por quadra e horário.',
    supportsGrouping: false,
    filters: [
      { key: 'courtId', label: 'Quadra', catalogKey: 'courts' },
      { key: 'weekday', label: 'Dia da semana', options: WEEKDAY_OPTIONS },
      { key: 'timeBand', label: 'Faixa de horário', options: TIME_BAND_OPTIONS },
    ],
  },
  {
    id: 'torneios',
    label: 'Torneios',
    description: 'Inscritos, capacidade, pagamentos e cancelamentos por categoria.',
    supportsGrouping: false,
    filters: [
      { key: 'tournamentId', label: 'Torneio', catalogKey: 'tournaments' },
      { key: 'category', label: 'Categoria', catalogKey: 'categories', parentKey: 'tournamentId' },
      { key: 'registrationStatus', label: 'Situação da inscrição', catalogKey: 'registrationStatuses' },
    ],
  },
];

export function areaDefinition(area: ReportArea): AreaDefinition {
  return AREAS.find((item) => item.id === area) ?? AREAS[0];
}

export function isReportArea(value: string | null): value is ReportArea {
  return AREAS.some((area) => area.id === value);
}
